import React, { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { useResponsive, useToggleState } from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useSweetpassCreditCards, useSweetpassPlusPlans } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import { LoadingAnimation } from '../LoadingAnimation';
import { Modal } from '../Modal';
import { SweetpassSwitchSubscriptionContent } from './SweetpassSwitchSubscriptionContent';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassSwitchSubscriptionModal = (
  props: SweetpassSwitchSubscriptionModalProps,
) => {
  const {
    currentPlanId = '',
    nextBillingDate = '',
    visible,
    onSubscriptionSwitch,
    onRequestClose,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  // ─── Queries ─────────────────────────────────────────────────────────

  const { plans, isLoadingPlans } = useSweetpassPlusPlans();
  const { creditCards, isFetchingSweetpassCreditCards } =
    useSweetpassCreditCards();
  const { data: contentfulData, fetching: isLoadingContentful } =
    useContentfulContentTypeEntry<PlanSwitchContentfulData>({
      contentType: 'sweetpassCheckoutPage',
    });

  const isLoadingContent =
    isLoadingPlans || isFetchingSweetpassCreditCards || isLoadingContentful;

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isAddingCreditCard,
    toggleOn: showCreditCardForm,
    toggleOff: hideCreditCardForm,
  } = useToggleState();

  const modalTitle = isAddingCreditCard
    ? t('credit-card-form.add')
    : t('sweetpass.change-subscription.title');

  const termsAndConditions =
    contentfulData?.fields?.planSwitchTermsAndConditions ??
    t('sweetpass.change-subscription.terms-fallback');

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnClose = useCallback(() => {
    onRequestClose();
    hideCreditCardForm();
  }, [hideCreditCardForm, onRequestClose]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      fitHeight
      visible={visible}
      accessibilityLabel={modalTitle}
      presentationStyle="pageSheet"
      size={match(['full', 'medium'])}
      onRequestClose={handleOnClose}
    >
      <Modal.Header
        onGoBack={isAddingCreditCard ? hideCreditCardForm : undefined}
        onClose={handleOnClose}
      >
        {modalTitle}
      </Modal.Header>

      <ScrollView>
        {isLoadingContent ? (
          <LoadingAnimation />
        ) : (
          <SweetpassSwitchSubscriptionContent
            currentPlanId={currentPlanId}
            nextBillingDate={nextBillingDate}
            plans={plans}
            creditCards={creditCards}
            isAddingCreditCard={isAddingCreditCard}
            termsAndConditions={termsAndConditions}
            showCreditCardForm={showCreditCardForm}
            hideCreditCardForm={hideCreditCardForm}
            onSubscriptionSwitch={onSubscriptionSwitch}
            onRequestClose={onRequestClose}
          />
        )}
      </ScrollView>
    </Modal>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type SweetpassSwitchSubscriptionModalProps = Readonly<{
  currentPlanId?: string;
  nextBillingDate?: string;
  visible: boolean;
  onSubscriptionSwitch?: () => void;
  onRequestClose: () => void;
}>;

type PlanSwitchContentfulData = Readonly<{
  planSwitchTermsAndConditions?: string;
}>;
